import React from "react";
import DOMPurify from "dompurify";
import { Divider, Skeleton } from "@jem-open/jem-ui";
import { NewsArticle } from "../services/model";


const ArticleImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
}> = ({ src, alt, className = "" }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);

  if (error) {
    return (
      <div
        className={`overflow-hidden rounded-lg bg-secondary-surface-lighter ${className}`}
      />
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-lg ${className}`}>
      {!loaded && <Skeleton className="absolute inset-0 h-full w-full" />}
      <img
        src={src}
        alt={alt}
        className={`h-full w-full object-cover transition-opacity ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
};

const ALLOWED_TAGS = ["p", "b", "strong", "ol", "ul", "a"];

const sanitizeHtml = (html: string): string => {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["href"],
  });
  DOMPurify.removeHooks("afterSanitizeAttributes");
  return clean;
};

const RichTextBody: React.FC<{ html: string }> = ({ html }) => (
  <div
    className="space-y-3 text-sm leading-relaxed text-greyscale-text-body [&_a]:text-primary-default [&_a]:underline [&_li]:ml-4 [&_li]:list-disc [&_ul]:space-y-1"
    // eslint-disable-next-line react/no-danger -- sanitized with DOMPurify
    dangerouslySetInnerHTML={{
      __html: sanitizeHtml(html),
    }}
  />
);

const HeroCard: React.FC<{ article: NewsArticle }> = ({ article }) => (
  <div className="mb-4 max-w-[1300px] rounded-xl bg-greyscale-surface-subtle p-8">
    <div className="flex items-center gap-8 max-[1100px]:flex-col-reverse">
      <div className="flex-1">
        <p className="mb-2 text-sm text-secondary-pink-900">{article.date}</p>
        <h3 className="mb-4 text-xl font-semibold text-greyscale-text-title">
          {article.title}
        </h3>
        <RichTextBody html={article.description} />
      </div>
      <ArticleImage
        src={article.image}
        alt={article.title}
        className="h-[350px] w-1/2 flex-shrink-0 max-[1100px]:h-[200px] max-[1100px]:w-full"
      />
    </div>
  </div>
);

const ArticleCard: React.FC<{ article: NewsArticle }> = ({ article }) => (
  <div className="max-w-[1300px] py-8">
    <div className="flex gap-8 max-[1100px]:flex-col">
      <ArticleImage
        src={article.image}
        alt={article.title}
        className="h-[265px] w-2/5 flex-shrink-0 max-[1100px]:h-[200px] max-[1100px]:w-full"
      />
      <div className="flex flex-1 flex-col justify-center pr-14 max-[1100px]:pr-0">
        <p className="mb-2 text-sm text-secondary-pink-900">{article.date}</p>
        <h3 className="mb-2 text-lg font-semibold text-greyscale-text-title">
          {article.title}
        </h3>
        <RichTextBody html={article.description} />
      </div>
    </div>
  </div>
);

interface NewsFeedProps {
  articles?: NewsArticle[];
}

const NewsFeed: React.FC<NewsFeedProps> = ({ articles = [] }) => {
  if (articles.length === 0) {
    return null;
  }

  const [hero, ...rest] = articles;

  return (
    <div className="py-6">
      <h2 className="mb-6 text-2xl font-semibold text-greyscale-text-title">
        Latest updates from Jem
      </h2>
      <HeroCard article={hero} />
      {rest.map((article, index) => (
        <React.Fragment key={article.title}>
          {index > 0 && <Divider />}
          <ArticleCard article={article} />
        </React.Fragment>
      ))}
    </div>
  );
};

export { NewsFeed };
